import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { BUSINESS } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim() || (!form.email.trim() && !form.phone.trim())) {
      toast.error("Please provide your name, a message, and an email or phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/messages`, form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      console.error("Failed to send message:", err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const infoCards = [
    { icon: Phone, label: "Phone", value: BUSINESS.phone, href: BUSINESS.phoneHref },
    { icon: Mail, label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
    { icon: MapPin, label: "Address", value: BUSINESS.address, href: BUSINESS.mapUrl },
  ];

  return (
    <div data-testid="contact-page">
      <section className="bg-zinc-950 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-red-600/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-red-500 mb-4">
            <span className="w-8 h-[2px] bg-red-500 inline-block" /> Get In Touch
          </span>
          <h1 className="font-heading text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-5">
            Contact <span className="text-red-500">Us</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed">
            Questions, estimates, or towing — reach out and we'll respond fast.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Info cards */}
          <div className="grid sm:grid-cols-3 gap-6 mb-14">
            {infoCards.map((c) => (
              <a
                key={c.label}
                href={c.href}
                target={c.label === "Address" ? "_blank" : undefined}
                rel="noreferrer"
                className="group bg-zinc-50 border border-zinc-100 rounded-xl p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`contact-card-${c.label.toLowerCase()}`}
              >
                <div className="w-12 h-12 rounded-lg bg-red-600 text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <c.icon size={22} />
                </div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">{c.label}</p>
                <p className="font-semibold text-zinc-900 break-words">{c.value}</p>
              </a>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Form */}
            <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-8 sm:p-10">
              <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-7">Send Us A Message</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="space-y-2">
                  <Label htmlFor="c-name" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Name *</Label>
                  <Input id="c-name" data-testid="contact-name-input" placeholder="Your name" value={form.name} onChange={update("name")} className="h-12 bg-white" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="c-email" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Email</Label>
                    <Input id="c-email" type="email" data-testid="contact-email-input" placeholder="you@email.com" value={form.email} onChange={update("email")} className="h-12 bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-phone" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Phone</Label>
                    <Input id="c-phone" data-testid="contact-phone-input" placeholder="(330) 555-0123" value={form.phone} onChange={update("phone")} className="h-12 bg-white" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-message" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Message *</Label>
                  <Textarea id="c-message" data-testid="contact-message-input" placeholder="How can we help?" rows={6} value={form.message} onChange={update("message")} className="bg-white" />
                </div>
                <Button type="submit" disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide text-sm h-13 h-14 px-9 rounded-md shadow-md hover:shadow-xl transition-all duration-200" data-testid="contact-submit-btn">
                  {submitting ? "Sending..." : <>Send Message <Send size={15} className="ml-1.5" /></>}
                </Button>
              </form>
            </div>

            {/* Map + hours */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden border border-zinc-100 shadow-sm flex-1 min-h-[320px]">
                <iframe
                  title="Caliber Auto Repair location"
                  src="https://www.google.com/maps?q=500+Golden+Oak+Pkwy,+Bedford,+OH+44146&output=embed"
                  className="w-full h-full min-h-[320px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>
              <div className="bg-zinc-900 rounded-2xl p-8 text-white">
                <div className="flex items-center gap-3 mb-5">
                  <Clock size={20} className="text-red-500" />
                  <h3 className="font-heading text-xl font-bold">Business Hours</h3>
                </div>
                <div className="space-y-3">
                  {BUSINESS.hours.map((h) => (
                    <div key={h.day} className="flex items-center justify-between text-sm border-b border-zinc-800 pb-3 last:border-0 last:pb-0">
                      <span className="text-zinc-300 font-medium">{h.day}</span>
                      <span className={h.time === "Closed" ? "text-red-400 font-semibold" : "text-zinc-400"}>{h.time}</span>
                    </div>
                  ))}
                </div>
                <a href={BUSINESS.mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-bold uppercase tracking-wide mt-6 transition-colors" data-testid="contact-directions-link">
                  Get Directions <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
